"use client";

import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import {
  Phone,
  ChevronLeft,
  ChevronRight,
  MapPin,
  ChevronDown,
  ChevronUp,
  Download,
  Smartphone,
  X,
  Calendar,
  MessageCircle,
} from "lucide-react";
import { AiFillInstagram } from "react-icons/ai";
import "./landingpage.css";
import { BsInstagram } from "react-icons/bs";

// ─── Business Configuration ────────────────────────────────────────────────
const businessInfo = {
  name: "GOSÉ HAİR STUDİO",
  slogan: "Kadın Kuaförü · Pamukkale / Denizli",
  phone: "902582638800",
  phoneFormatted: "(0258) 263 88 00",
  whatsapp: "905457977978",
  instagram: "gosedenizli",
  address: "Kınıklı, 6093. Sk. no:3/A, 20160 Pamukkale/Denizli",
  hours: "Her Gün 09:00 – 19:00",
  mapEmbed:
    "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3154.8710206760097!2d29.098577!3d37.74617!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14c73fe7cf490739%3A0xf602b830400ca827!2sGOS%C3%89%20Hair%20%2F%20Make%20Up%20%2F%20Beauty!5e0!3m2!1str!2str!4v1777981172324!5m2!1str!2str",
};

// ─── Services ────────────────────────────────────────────────────────────────
const services = [
  { id: 1, name: "SAÇ KESİM", sub: "Modern & Klasik Teknikler", image: "/images/moon/img_1.png" },
  { id: 2, name: "OMBRE & SOMBRE", sub: "Doğal Renk Geçişleri", image: "/images/moon/img.png" },
  { id: 3, name: "GELİN MAKYAJİ", sub: "Özel Gün Tasarımları", image: "/images/moon/img_2.png" },
  { id: 4, name: "KAYNAK & UZATMA", sub: "Doğal Görünümlü Kaynak", image: "/images/moon/img_1.png" },
  { id: 5, name: "ŞEKİLLENDİRME", sub: "Davet & Günlük Styling", image: "/images/moon/img_5.png" },
  { id: 6, name: "BAKIM & ONARMA", sub: "Protein & Derin Bakım", image: "/images/moon/img.png" },
];

// ─── Team ─────────────────────────────────────────────────────────────────────
const teamMembers = [
  {
    id: 1,
    name: "Burak Kaya",
    specialty: "Uzman Stilist",
    exp: "10+ Yıl Deneyim",
    img: "/images/moon/personel_1.jpg",
    ig: "moon_hair_studioo",
  },
  {
    id: 2,
    name: "Merve Arslan",
    specialty: "Renk & Ombre Uzmanı",
    exp: "7+ Yıl Deneyim",
    img: "/images/moon/personel_2.jpg",
    ig: "moon_hair_studioo",
  },
  {
    id: 3,
    name: "Ayşe Doğan",
    specialty: "Gelin & Özel Gün Stili",
    exp: "5+ Yıl Deneyim",
    img: "/images/moon/personel_3.jpg",
    ig: "moon_hair_studioo",
  },
];

// ─── Gallery Items ────────────────────────────────────────────────────────────
const galleryItems = [
  { id: 1, type: "image", url: "/images/moon/img.png", alt: "Ombre Renklendirme" },
  { id: 2, type: "video", url: "/images/moon/bg_vid_moon.mp4", alt: "Saç Kesim" },
  { id: 3, type: "image", url: "/images/moon/img_5.png", alt: "Şekillendirme" },
  { id: 4, type: "image", url: "/images/moon/img_2.png", alt: "Özel Gün" },
  { id: 5, type: "image", url: "/images/moon/img_1.png", alt: "Renklendirme" },
];

// ─── Main Component ───────────────────────────────────────────────────────────
const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [hoveredService, setHoveredService] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installModalVisible, setInstallModalVisible] = useState(false);
  const [heroLoaded, setHeroLoaded] = useState(false);

  const gallerySliderRef = useRef(null);
  const circleRef = useRef(null);

  // ── Hero load ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => setHeroLoaded(true), 300);
    return () => clearTimeout(t);
  }, []);

  // ── PWA Install Prompt ────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstallClick = async () => {
    setInstallModalVisible(true);
    if (deferredPrompt) {
      deferredPrompt.prompt();
      deferredPrompt.userChoice.then(() => setDeferredPrompt(null));
    }
  };

  // ── Scroll Tracking ───────────────────────────────────────────────────────
  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "home",
        "services",
        "team",
        "gallery",
        "about",
        "contact",
      ];
      const scrollPosition = window.scrollY + 100;
      setShowScrollTop(window.scrollY > 300);

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(section);
            break;
          }
        }
      }

      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY;
      const scrollableHeight = documentHeight - windowHeight;
      if (scrollableHeight > 0 && circleRef.current) {
        const scrollPercent = scrollTop / scrollableHeight;
        const circumference = 2 * Math.PI * 26;
        circleRef.current.style.strokeDashoffset =
          circumference - scrollPercent * circumference;
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleBookAppointment = () => navigate("/auth/sign-in");
  const handleWhatsapp = () =>
    window.open(`https://wa.me/${businessInfo.whatsapp}`, "_blank");

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      setActiveSection(sectionId);
      setMenuOpen(false);
    }
  };

  const scrollToNext = () => {
    const el = document.getElementById("services");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const scrollGallery = (dir) => {
    if (!gallerySliderRef.current) return;
    const amount = window.innerWidth > 768 ? 500 : window.innerWidth * 0.88;
    gallerySliderRef.current.scrollBy({
      left: dir === "left" ? -amount : amount,
      behavior: "smooth",
    });
  };

  const navLinks = [
    { id: "home", text: "Ana Sayfa" },
    { id: "services", text: "Hizmetler" },
    { id: "team", text: "Ekibimiz" },
    { id: "gallery", text: "Galeri" },
    { id: "about", text: "Hakkımızda" },
    { id: "contact", text: "İletişim" },
  ];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="mhs-landing">
      {/* ══════════════════════════════════════════════════════════════════
                HAMBURGER BUTTON
            ══════════════════════════════════════════════════════════════════ */}
      <button
        className={`mhs-hamburger ${menuOpen ? "open" : ""}`}
        onClick={() => setMenuOpen(!menuOpen)}
        aria-label="Menü"
      >
        <span className="mhs-ham-line" />
        <span className="mhs-ham-line" />
        <span className="mhs-ham-line" />
      </button>

      {/* ══════════════════════════════════════════════════════════════════
                FULLSCREEN OVERLAY MENU
            ══════════════════════════════════════════════════════════════════ */}
      <div className={`mhs-overlay-menu ${menuOpen ? "open" : ""}`}>
        <div className="mhs-overlay-inner">
          <div className="mhs-overlay-brand">GOSÉ Hair Studio</div>
          <nav className="mhs-overlay-nav">
            {navLinks.map((link, i) => (
              <button
                key={link.id}
                className={`mhs-overlay-link ${
                  activeSection === link.id ? "active" : ""
                }`}
                onClick={() => scrollToSection(link.id)}
                style={{ animationDelay: `${i * 0.07}s` }}
              >
                <span className="mhs-overlay-num">0{i + 1}</span>
                {link.text}
              </button>
            ))}
          </nav>
          <div className="mhs-overlay-bottom">
            <button className="mhs-overlay-cta" onClick={handleBookAppointment}>
              <Calendar size={16} />
              Randevu Al
            </button>
            <div className="mhs-overlay-socials">
              <a
                href={`https://www.instagram.com/${businessInfo.instagram}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <BsInstagram size={20} />
              </a>
              <a
                href={`https://wa.me/${businessInfo.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="WhatsApp"
              >
                <MessageCircle size={20} />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════
                HERO SECTION
            ══════════════════════════════════════════════════════════════════ */}
      <section id="home" className="mhs-hero">
        <div className="mhs-hero-img-wrap">
          <video
            className="mhs-hero-img"
            autoPlay
            muted
            loop
            playsInline
            src="/images/moon/bg_vid_moon.mp4"
          />
        </div>
        <div className="mhs-hero-overlay" />

        <div className={`mhs-hero-content ${heroLoaded ? "visible" : ""}`}>
          <p className="mhs-hero-eyebrow">Pamukkale · Denizli</p>
          <h1 className="mhs-hero-title">
            <span>GOSÉ</span>
            <em>HAIR</em>
            <span>STUDIO</span>
          </h1>
          <p className="mhs-hero-sub">Güzelliğin en zarif hali</p>
          <button className="mhs-hero-btn" onClick={handleBookAppointment}>
            <Calendar size={16} />
            Randevu Al
          </button>
        </div>

        <div
          className="mhs-scroll-hint"
          onClick={scrollToNext}
          role="button"
          tabIndex={0}
        >
          <ChevronDown size={22} />
          <span>Kaydır</span>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════��═════════════
                STATS STRIP
            ══════════════════════════════════════════════════════════════════ */}
      <section className="mhs-stats">
        <div className="mhs-stats-inner">
          <div className="mhs-stat-item">
            <span className="mhs-stat-num">500+</span>
            <span className="mhs-stat-label">Mutlu Müşteri</span>
          </div>
          <div className="mhs-stat-divider" />
          <div className="mhs-stat-item">
            <span className="mhs-stat-num">3</span>
            <span className="mhs-stat-label">Uzman Stilist</span>
          </div>
          <div className="mhs-stat-divider" />
          <div className="mhs-stat-divider" />
          <div className="mhs-stat-item">
            <span className="mhs-stat-num">7/7</span>
            <span className="mhs-stat-label">Her Gün Açık</span>
          </div>
        </div>
      </section>

      {/* ══════════════════════════���═══════════════════════════════════════
                SERVICES — Typography Hover Effect
            ══════════════════════════════════════════════════════════════════ */}
      <section id="services" className="mhs-services-section">
        {services.map((service) => (
          <div
            key={`bg-layer-${service.id}`}
            className="mhs-services-bg-layer"
            style={{
              backgroundImage: `url(${service.image})`,
              opacity: hoveredService?.id === service.id ? 1 : 0,
            }}
          />
        ))}
        <div
          className="mhs-services-bg-dim"
          style={{ opacity: hoveredService ? 1 : 0 }}
        />

        <div className="mhs-section-header">
          <span className="mhs-section-tag">Hizmetlerimiz</span>
          <h2 className="mhs-section-title">Ne Yapıyoruz?</h2>
        </div>

        <div className="mhs-styles-list">
          {services.map((service) => (
            <div
              key={service.id}
              className={`mhs-style-item ${
                hoveredService?.id === service.id ? "hovered" : ""
              }`}
              onMouseEnter={() => setHoveredService(service)}
              onMouseLeave={() => setHoveredService(null)}
              onTouchStart={() =>
                setHoveredService(
                  hoveredService?.id === service.id ? null : service
                )
              }
            >
              <span className="mhs-style-num">0{service.id}</span>
              <span className="mhs-style-name">{service.name}</span>
              <span className="mhs-style-sub">{service.sub}</span>
              <span className="mhs-style-arrow">→</span>
            </div>
          ))}
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
                TEAM SECTION
            ══════════════════════════════════════════════════════════════════ */}
      <section id="team" className="mhs-team-section">
        <div className="mhs-section-header">
          <span className="mhs-section-tag">Ekibimiz</span>
          <h2 className="mhs-section-title">Uzman Stilistler</h2>
          <p className="mhs-section-sub">Her biri kendi alanının uzmanı</p>
        </div>

        <div className="mhs-team-grid">
          {teamMembers.map((member) => (
            <div key={member.id} className="mhs-team-card">
              <div className="mhs-team-img-wrap">
                <img
                  src={member.img}
                  alt={member.name}
                  className="mhs-team-img"
                  loading="lazy"
                />
                <div className="mhs-team-img-overlay" />
              </div>
              <div className="mhs-team-info">
                <h3 className="mhs-team-name">{member.name}</h3>
                <span className="mhs-team-specialty">{member.specialty}</span>
                <span className="mhs-team-exp">{member.exp}</span>
                <a
                  href={`https://www.instagram.com/${member.ig}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mhs-team-ig"
                >
                  <BsInstagram size={13} />@{member.ig}
                </a>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
                GALLERY — Horizontal Scroll
            ══════════════════════════════════════════════════════════════════ */}
      <section id="gallery" className="mhs-gallery-section">
        <div className="mhs-section-header">
          <span className="mhs-section-tag">Çalışmalarımız</span>
          <h2 className="mhs-section-title">Galeri</h2>
        </div>

        <div className="mhs-gallery-wrap">
          <button
            className="mhs-gallery-btn left"
            onClick={() => scrollGallery("left")}
            aria-label="Önceki"
          >
            <ChevronLeft size={22} />
          </button>

          <div className="mhs-gallery-track" ref={gallerySliderRef}>
            {galleryItems.map((item) => (
              <div
                key={item.id}
                className="mhs-gallery-item"
                onClick={() => setSelectedImage(item)}
              >
                {item.type === "video" ? (
                  <video
                    src={item.url}
                    className="mhs-gallery-media"
                    muted
                    loop
                    playsInline
                    autoPlay
                    preload="metadata"
                  />
                ) : (
                  <img
                    src={item.url}
                    alt={item.alt || "GOSÉ Hair Studio"}
                    loading="lazy"
                    className="mhs-gallery-media"
                  />
                )}
                <div className="mhs-gallery-item-overlay">
                  <span className="mhs-gallery-item-label">{item.alt}</span>
                </div>
              </div>
            ))}
          </div>

          <button
            className="mhs-gallery-btn right"
            onClick={() => scrollGallery("right")}
            aria-label="Sonraki"
          >
            <ChevronRight size={22} />
          </button>
        </div>

        {/* Lightbox */}
        {selectedImage && (
          <div className="mhs-lightbox" onClick={() => setSelectedImage(null)}>
            <button
              className="mhs-lightbox-close"
              onClick={() => setSelectedImage(null)}
              aria-label="Kapat"
            >
              <X size={26} />
            </button>
            <div
              className="mhs-lightbox-inner"
              onClick={(e) => e.stopPropagation()}
              onContextMenu={(e) => e.preventDefault()}
            >
              {selectedImage.type === "video" ? (
                <video
                  src={selectedImage.url}
                  className="mhs-lightbox-media"
                  controls
                  autoPlay
                  playsInline
                  loop
                />
              ) : (
                <img
                  src={selectedImage.url}
                  alt={selectedImage.alt}
                  className="mhs-lightbox-media"
                  draggable="false"
                />
              )}
              {selectedImage.alt && (
                <p className="mhs-lightbox-caption">{selectedImage.alt}</p>
              )}
            </div>
          </div>
        )}
      </section>

      {/* ══════════════════════════════════════════════════════════════════
                MOTTO / QUOTE SECTION
            ══════════════════════════════════════════════════════════════════ */}
      <section className="mhs-motto-section">
        <div className="mhs-motto-inner">
          <span className="mhs-motto-quote">&ldquo;</span>
          <p className="mhs-motto-text">
            Güzellik bir seçimdir. Biz o seçimi mükemmelleştiriyoruz.
          </p>
          <span className="mhs-motto-author">— GOSÉ Hair Studio</span>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
                ABOUT SECTION
            ══════════════════════════════════════════════════════════════════ */}
      <section id="about" className="mhs-about-section">
        <div className="mhs-about-grid">
          <div className="mhs-about-content">
            <span className="mhs-section-tag">Hakkımızda</span>
            <h2 className="mhs-section-title left">Hikayemiz</h2>
            <h3 className="mhs-about-heading">GOSÉ Hair Studio</h3>
            <p className="mhs-about-text">
              Pamukkale&apos;nin kalbinde, güzelliğin sanatını yaşatıyoruz. Saç
              kesiminden ombre renklendirmeye, gelin makyajından saç kaynağına
              uzanan geniş hizmet yelpazemizle her müşterimize özel ve unutulmaz
              bir deneyim sunuyoruz.
            </p>
            <p className="mhs-about-text">
              Hijyen ve kaliteyi ön planda tutarak, her ziyarette tutarlı ve
              premium bir deneyim yaşatmayı taahhüt ediyoruz. Çünkü siz buna
              layıksınız.
            </p>
            <div className="mhs-about-features">
              <div className="mhs-feature-row">
                <span className="mhs-feature-dot" />
                <span>Kişiye Özel Stil Danışmanlığı</span>
              </div>
              <div className="mhs-feature-row">
                <span className="mhs-feature-dot" />
                <span>Premium Ürünler ile Uygulama</span>
              </div>
              <div className="mhs-feature-row">
                <span className="mhs-feature-dot" />
                <span>Online Randevu Sistemi</span>
              </div>
            </div>
          </div>
          <div className="mhs-about-image-wrap">
            <img
              src="/images/moon/img_6.png"
              alt="GOSÉ Hair Studio Salonu"
              className="mhs-about-img"
            />
            <div className="mhs-about-img-line" />
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
                CONTACT SECTION
            ══════════════════════════════════════════════════════════════════ */}
      <section id="contact" className="mhs-contact-section">
        <div className="mhs-section-header">
          <span className="mhs-section-tag">İletişim</span>
          <h2 className="mhs-section-title">Bize Ulaşın</h2>
          <p className="mhs-section-sub">Randevu ve bilgi için</p>
        </div>

        <div className="mhs-contact-cards">
          <a href={`tel:${businessInfo.phone}`} className="mhs-contact-card">
            <div className="mhs-contact-icon">
              <Phone size={26} />
            </div>
            <h3>Telefon</h3>
            <span>{businessInfo.phoneFormatted}</span>
          </a>
          <a
            href={`https://wa.me/${businessInfo.whatsapp}`}
            target="_blank"
            rel="noopener noreferrer"
            className="mhs-contact-card"
          >
            <div className="mhs-contact-icon mhs-wa-icon">
              <MessageCircle size={26} />
            </div>
            <h3>WhatsApp</h3>
            <span>Mesaj Gönder</span>
          </a>
          <a
            href={`https://www.instagram.com/${businessInfo.instagram}`}
            target="_blank"
            rel="noopener noreferrer"
            className="mhs-contact-card"
          >
            <div className="mhs-contact-icon mhs-ig-icon">
              <AiFillInstagram size={26} />
            </div>
            <h3>Instagram</h3>
            <span>@{businessInfo.instagram}</span>
          </a>
        </div>

        <div className="mhs-contact-info-row">
          <div className="mhs-info-block">
            <MapPin size={20} className="mhs-info-icon" />
            <div>
              <h4>Adres</h4>
              <p>{businessInfo.address}</p>
            </div>
          </div>
          <div className="mhs-info-block">
            <Calendar size={20} className="mhs-info-icon" />
            <div>
              <h4>Çalışma Saatleri</h4>
              <p>{businessInfo.hours}</p>
            </div>
          </div>
        </div>

        <div className="mhs-map-wrap">
          <iframe
            src={businessInfo.mapEmbed}
            width="100%"
            height="100%"
            style={{ border: 0, filter: "saturate(0.3) brightness(1.05)" }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="GOSÉ Hair Studio Konum"
          />
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
                CALL TO ACTION — Full Width
            ══════════════════════════════════════════════════════════════════ */}
      <section className="mhs-cta-section">
        <div className="mhs-cta-inner">
          <span className="mhs-cta-tag">Hayalinizdeki Saçlara</span>
          <h2 className="mhs-cta-title">
            Şimdi
            <br />
            Randevu
            <br />
            Alın
          </h2>
          <p className="mhs-cta-sub">
            Sizin için en uygun saati birlikte belirleyelim.
          </p>
          <button className="mhs-cta-btn" onClick={handleBookAppointment}>
            <Calendar size={18} />
            Online Randevu
          </button>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
                FOOTER
            ══════════════════════════════════════════════════════════════════ */}
      <footer className="mhs-footer">
        <div className="mhs-footer-inner">
          <div className="mhs-footer-brand">
            <span className="mhs-footer-logo-text">GOSÉ Hair Studio</span>
            <p className="mhs-footer-slogan">{businessInfo.slogan}</p>
          </div>
          <div className="mhs-footer-copy">
            <p>© 2025 GOSÉ Hair Studio. Tüm hakları saklıdır.</p>
            <p className="mhs-footer-powered">
              Powered by{" "}
              <a
                href="https://www.vireliosoft.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                Vireliosoft
              </a>{" "}
              /{" "}
              <a
                href="https://www.randevupp.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                RandevUpp
              </a>
            </p>
          </div>
        </div>
      </footer>

      {/* ══════════════════════════════════════════════════════════════════
                SCROLL TO TOP
            ══════════════════════════════════════════════════════════════════ */}
      {showScrollTop && (
        <button
          className="mhs-scroll-top"
          onClick={scrollToTop}
          aria-label="Yukarı Çık"
        >
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 60 60"
            className="mhs-progress-ring"
          >
            <circle
              stroke="rgba(60,40,30,0.1)"
              strokeWidth="4"
              fill="transparent"
              r="26"
              cx="30"
              cy="30"
            />
            <circle
              ref={circleRef}
              stroke="#b8977a"
              strokeWidth="4"
              fill="transparent"
              r="26"
              cx="30"
              cy="30"
              style={{
                strokeDasharray: `${2 * Math.PI * 26}`,
                strokeDashoffset: `${2 * Math.PI * 26}`,
              }}
            />
          </svg>
          <ChevronUp size={20} className="mhs-scroll-icon" />
        </button>
      )}

      {/* ══════════════════════════════════════════════════════════════════
                MOBILE BOTTOM BAR
            ══════════════════════════════════════════════════════════════════ */}
      <div className="mhs-bottom-bar">
        <div className="mhs-bottom-pill">
          <button
            className="mhs-bottom-btn install-btn"
            onClick={handleInstallClick}
          >
            <Download size={18} />
            <span>İndir</span>
          </button>
          <div className="mhs-bottom-divider" />
          <button
            className="mhs-bottom-btn appt-btn"
            onClick={handleBookAppointment}
          >
            <Calendar size={18} />
            <span>Randevu</span>
          </button>
          <div className="mhs-bottom-divider" />
          <button className="mhs-bottom-btn wa-btn" onClick={handleWhatsapp}>
            <MessageCircle size={18} />
            <span>WhatsApp</span>
          </button>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════
                INSTALL MODAL
            ══════════════════════════════════════════════════════════════════ */}
      {installModalVisible && (
        <div
          className="mhs-modal-overlay"
          onClick={() => setInstallModalVisible(false)}
        >
          <div
            className="mhs-install-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mhs-modal-handle" />
            <div className="mhs-modal-header">
              <h5>Uygulamayı Yükle</h5>
              <button
                className="mhs-modal-close"
                onClick={() => setInstallModalVisible(false)}
                aria-label="Kapat"
              >
                <X size={20} />
              </button>
            </div>
            <div className="mhs-modal-body">
              <div className="mhs-modal-step">
                <div className="mhs-modal-step-icon">
                  <Smartphone size={22} />
                </div>
                <div>
                  <strong>iPhone / Safari</strong>
                  <p>Paylaş butonuna dokun → &quot;Ana Ekrana Ekle&quot; seç</p>
                </div>
              </div>
              <div className="mhs-modal-step">
                <div className="mhs-modal-step-icon">
                  <Download size={22} />
                </div>
                <div>
                  <strong>Android / Chrome</strong>
                  <p>Menü (⋮) → &quot;Ana Ekrana Ekle&quot; seç</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
